import React, { FC, JSX, useCallback, useMemo } from 'react';
import { useParams } from 'react-router';

import { EvButton, EvMainTitle } from '@evinced-private/ui-common';

import { useConfiguration } from 'src/providers/configurationProvider/ConfigurationProvider';
import { IPropertyViewContext, usePropertyViewContext } from 'src/providers/PropertyViewProvider';
import { SiteScannerUiToggles } from 'src/services/LocalTogglesService';

import { withTabsNavigation } from '../../hoc/withTabsNavigation';
import { TabsViews } from '../../types/TabsViews';

import { ScanBreakdownView } from './views/breakdown/ScanBreakdownViewV2';
import { ScanComponentsView } from './views/components/ScanComponentsViewV2';
import { ScanIssueTypesView } from './views/issues/ScanIssueTypesViewV2';
import { ScanOverviewViewV3 } from './views/overview/ScanOverviewViewV3';
import { ScanPagesView } from './views/pages/ScanPagesViewV2';

import './ScanAnalysisPage.scss';

interface IScanAnalysisPageProps {
	view: TabsViews;
}

// todo: change to ScanPage once we'll remove the old one
const ScanAnalysisPage: FC<IScanAnalysisPageProps> = ({ view }: IScanAnalysisPageProps) => {
	const { propertyId, scanId } = useParams<{ [key: string]: string }>();
	const { getToggle } = useConfiguration();
	const propertyViewContext: IPropertyViewContext = usePropertyViewContext();
	const viewId = useMemo(() => propertyViewContext?.currentView?.id, [propertyViewContext]);

	const showPrintButton = useMemo(() => {
		return getToggle(SiteScannerUiToggles.ENABLE_SCAN_OVERVIEW_PRINT_BUTTON);
	}, [getToggle]);

	const onDownloadPDFClick = useCallback((): void => {
		const currentPageTitle = document.title;
		// set a custom name for the pdf file
		document.title = `${propertyId}-Scan-${scanId}`;
		window.print();
		// return to previous page title
		document.title = currentPageTitle;
	}, [propertyId, scanId]);

	const renderActionButtons = useCallback((): JSX.Element => {
		const isOverview = view === TabsViews.OVERVIEW;
		return (
			<div className="actions">
				{showPrintButton && isOverview && (
					<EvButton ariaLabel="Download PDF" onClick={onDownloadPDFClick}>
						Print \ Save as PDF
					</EvButton>
				)}
			</div>
		);
	}, [showPrintButton, onDownloadPDFClick, view]);

	const renderViewContent = (): JSX.Element => {
		switch (view) {
			case TabsViews.OVERVIEW: {
				return (
					<ScanOverviewViewV3
						scanId={scanId}
						propertyId={propertyId}
						renderActionButtons={renderActionButtons}
						viewId={viewId}
					/>
				);
			}

			case TabsViews.COMPONENTS: {
				return <ScanComponentsView viewId={viewId} scanId={scanId} propertyId={propertyId} />;
			}

			case TabsViews.ISSUE_TYPES: {
				return <ScanIssueTypesView viewId={viewId} scanId={scanId} propertyId={propertyId} />;
			}

			case TabsViews.PAGES: {
				return <ScanPagesView viewId={viewId} scanId={scanId} propertyId={propertyId} />;
			}

			case TabsViews.BREAKDOWN: {
				return <ScanBreakdownView viewId={viewId} scanId={scanId} propertyId={propertyId} />;
			}
			default:
				return <div />;
		}
	};

	return (
		<div className="scan-analysis-page">
			<div className="scan-page-top">
				<EvMainTitle className="sr-only">{view}</EvMainTitle>
			</div>
			{renderViewContent()}
		</div>
	);
};

const ScanAnalysisPageWithTabs = withTabsNavigation(ScanAnalysisPage);

export { ScanAnalysisPageWithTabs };
